import { useEffect, useState } from "react";
import api from "../services/api.tsx";
import { useCart } from "../contexts/CartContext.tsx";
import Product from "../Entities/Product";
import Campaign from "../Entities/Campaign";
import ProductCard from "../components/ProductCard.tsx";
import { useError } from "../contexts/ErrorContext.tsx";
import { getDiscountedPrice } from "../utils/price.tsx";
import { useSuccess } from "../contexts/SuccessContext.tsx";
import CampaignCarousel from "../components/CampaignCarousel.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";

const ProductListPage = () => {
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filtered, setFiltered] = useState<Product[]>([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const { setError } = useError();
  const { addSuccess } = useSuccess();
  const { isAuthenticated } = useAuth();
  const [filterOpen, setFilterOpen] = useState(false);

  // Get unique categories and brands from products
  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
  const brands = Array.from(new Set(products.map(p => p.brand).filter(Boolean)));

  // Filter brands based on selected category
  const filteredBrands = category
    ? Array.from(new Set(products.filter(p => p.category === category).map(p => p.brand).filter(Boolean)))
    : brands;

  // Filter categories based on selected brand
  const filteredCategories = brand
    ? Array.from(new Set(products.filter(p => p.brand === brand).map(p => p.category).filter(Boolean)))
    : categories;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, campaignRes] = await Promise.all([
          api.get("/api/product"),
          api.get("/api/campaign"),
        ]);
        setProducts(productRes.data);
        setCampaigns(campaignRes.data);
        setFiltered(productRes.data);
      } catch (error) {
        setError(error.response?.data?.Message || "Bir hata oluştu.");
      }
    };
    fetchData();
  }, [setError]);

  useEffect(() => {
    let result = products;

    if (name && name.length >= 3)
      result = result.filter((p) =>
        p.name?.toLocaleLowerCase("tr").includes(name.toLocaleLowerCase("tr"))
      );
    if (category && category.length >= 3)
      result = result.filter((p) =>
        p.category?.toLocaleLowerCase("tr").includes(category.toLocaleLowerCase("tr"))
      );
    if (brand && brand.length >= 3)
      result = result.filter((p) =>
        p.brand?.toLocaleLowerCase("tr").includes(brand.toLocaleLowerCase("tr"))
      );

    if (maxPrice) result = result.filter((p) => p.price <= maxPrice);
    setFiltered(result);
  }, [name, category, brand, maxPrice, products]);

  const isCampaignActive = (campaign: Campaign) => {
    const now = new Date();
    return now >= new Date(campaign.startDate) && now <= new Date(campaign.endDate);
  };

  const getCampaignForProduct = (productId: string) => {
    return campaigns.find(
      (campaign) => campaign.productIds.includes(productId) && isCampaignActive(campaign)
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-2 md:px-8 py-6">
        <CampaignCarousel campaigns={campaigns.filter(isCampaignActive)} />

        {/* Mobilde filtre butonu */}
        <div className="md:hidden mb-4 flex justify-end">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded font-semibold"
            onClick={() => setFilterOpen(true)}
          >
            Filtrele
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mt-6">
          {/* Sol filtre paneli */}
          <aside className="hidden md:block md:col-span-1 bg-white rounded-xl shadow-md p-6 space-y-5 sticky top-24 h-fit">
            <h3 className="font-bold text-lg mb-2 text-blue-700">Filtrele</h3>
            <input
              type="text"
              placeholder="Ürün Adı"
              className="w-full p-2 border rounded"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <select
              className="w-full p-2 border rounded"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <option value="">Kategori Seç</option>
              {filteredCategories.map((cat, idx) => (
                <option key={idx} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              className="w-full p-2 border rounded"
              value={brand}
              onChange={e => setBrand(e.target.value)}
            >
              <option value="">Marka Seç</option>
              {filteredBrands.map((b, idx) => (
                <option key={idx} value={b}>{b}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Maksimum Fiyat"
              className="w-full p-2 border rounded"
              value={maxPrice || ""}
              onChange={(e) => setMaxPrice(Number(e.target.value))}
            />
            <button
              className="w-full bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 font-semibold"
              onClick={() => {
                setName("");
                setCategory("");
                setBrand("");
                setMaxPrice(null);
              }}
            >
              Filtreleri Temizle
            </button>
          </aside>

          {/* Mobil filtre paneli - modal */}
          {filterOpen && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center md:hidden">
              <div className="bg-white rounded-xl shadow-lg p-6 w-[90vw] max-w-sm mx-auto relative">
                <button
                  className="absolute top-2 right-2 text-gray-500 text-xl"
                  onClick={() => setFilterOpen(false)}
                  aria-label="Kapat"
                >
                  &times;
                </button>
                <h3 className="font-bold text-lg mb-2 text-blue-700">Filtrele</h3>
                <input
                  type="text"
                  placeholder="Ürün Adı"
                  className="w-full p-2 border rounded mb-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <select
                  className="w-full p-2 border rounded mb-2"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                >
                  <option value="">Kategori Seç</option>
                  {filteredCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  className="w-full p-2 border rounded mb-2"
                  value={brand}
                  onChange={e => setBrand(e.target.value)}
                >
                  <option value="">Marka Seç</option>
                  {filteredBrands.map((b, idx) => (
                    <option key={idx} value={b}>{b}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Maksimum Fiyat"
                  className="w-full p-2 border rounded mb-2"
                  value={maxPrice || ""}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                />
                <button
                  className="w-full bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 font-semibold mb-2"
                  onClick={() => {
                    setName("");
                    setCategory("");
                    setBrand("");
                    setMaxPrice(null);
                  }}
                >
                  Filtreleri Temizle
                </button>
                <button
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded font-semibold"
                  onClick={() => setFilterOpen(false)}
                >
                  Uygula
                </button>
              </div>
            </div>
          )}

          {/* Ürün listesi */}
          <main className="md:col-span-4">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((product) => {
                const campaign = getCampaignForProduct(product.id);
                const discountedPrice = campaign ? getDiscountedPrice(product.price, campaign.discountRate) : undefined;
                return (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    imageUrl={product.imageUrl}
                    price={product.price}
                    discountRate={campaign?.discountRate}
                    campaignName={
                      campaign
                        ? `%${(campaign.discountRate).toFixed(0)} indirim • Son: ${new Date(campaign.endDate).toLocaleDateString("tr-TR")}`
                        : undefined
                    }
                    campaignEndDate={campaign?.endDate}
                    brand={product.brand}
                    category={product.category}
                    participantCount={campaign?.participantCount}
                    maxUserCount={campaign?.maxUserCount}
                    isAuthenticated={isAuthenticated} // pass this
                    hasCampaign={!!campaign}
                    stock={product.stock}
                    onAddToCart={async (quantity) => {
                      try {
                        const response = await addToCart({
                          id: product.id,
                          productId: product.id,
                          name: product.name,
                          price: product.price,
                          stock: product.stock,
                          finalPrice: discountedPrice || product.price,
                          quantity,
                          ...(campaign && { campaignId: campaign.id }),
                          depositRate: campaign?.depositRate || 0
                        });
                        // Check if response is successful (adjust according to your API)
                        if (response?.success || response?.status === 200) {
                          addSuccess("Ürün sepete eklendi!");
                        }
                      } catch (err) {
                        // Optionally handle error
                        setError("Ürün sepete eklenemedi!");
                      }
                    }}
                  />
                );
              })}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductListPage;
